import { FC, useState } from 'react';
import {
  FormControl,
  InputAdornment,
  ListSubheader,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { FontGroup } from '../utils/fontUtils';

interface Props {
  value: string;
  fonts: FontGroup[];
  standardFonts: string[];
  customFonts: { fontFamily: string }[];
  onChange: (family: string) => void;
}

const getFontLabel = (
  family: string,
  standardFonts: string[],
  customFonts: { fontFamily: string }[],
) => {
  if (standardFonts.includes(family)) return ' (Standard)';
  if (customFonts.some((f) => f.fontFamily === family)) return ' (Custom)';
  return ' (Not installed)';
};

const containsText = (text: string, searchText: string) =>
  text.toLowerCase().includes(searchText.toLowerCase());

export const FontFamilySelect: FC<Props> = ({
  value,
  fonts,
  standardFonts,
  customFonts,
  onChange,
}) => {
  const [searchText, setSearchText] = useState('');

  const displayedFonts = searchText
    ? fonts.filter(({ family }) => containsText(family, searchText))
    : fonts;

  return (
    <FormControl fullWidth>
      <Select
        size="small"
        aria-label="Font family"
        value={value}
        onClose={() => setSearchText('')}
        displayEmpty
        renderValue={(selected) => {
          if (!selected) return <Typography color="text.secondary">Select Font Family</Typography>;
          const label = getFontLabel(selected as string, standardFonts, customFonts);
          return (
            <Typography>
              {selected as string} {label}
            </Typography>
          );
        }}
        MenuProps={{
          autoFocus: false,
          PaperProps: {
            style: {
              maxHeight: '300px',
              overflowY: 'auto',
            },
          },
        }}
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Search fonts..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') e.stopPropagation();
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {displayedFonts.map(({ family }) => {
          const label = getFontLabel(family, standardFonts, customFonts);
          return (
            <MenuItem key={family} value={family} onClick={() => onChange(family)}>
              {family} {label}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
};
